import { useMemo, useState } from 'react';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, day as dayText } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { useUi } from '../../lib/ui-state.tsx';
import { TONES, useAction, useSave } from '../manage/common.tsx';
import { Avatar } from './Directory.tsx';

/**
 * Skills and training. Every person has a level for the skills they know (set by themselves or
 * the people above them); the matrix shows who knows what. Courses are lists of lessons (reading,
 * a video link, a quiz the server marks); managers assign them with a last day, and passing one
 * can raise a skill.
 */

interface SkillType {
  id: string;
  name: string;
  levels: string[];
  color: number;
  skills: { id: string; name: string }[];
}
interface SkillsData {
  user: { id: string; name: string };
  types: SkillType[];
  skills: { skill: string; level: number; note: string | null; updated_at: string }[];
  can: { edit: boolean; manage: boolean };
}
interface MatrixData {
  types: SkillType[];
  people: { id: string; name: string; job_title: string | null; department: string | null; photo: string | null }[];
  levels: { user: string; skill: string; level: number }[];
  departments: { id: string; name: string }[];
}
interface CourseRow {
  id: string;
  title: string;
  description: string | null;
  color: number;
  minutes: number | null;
  pass_mark: number;
  skill: string | null;
  skill_level: number | null;
  active: boolean;
  lessons: number;
  quizzes: number;
  skill_name: string | null;
  skill_level_name: string | null;
  mine: { id: string; state: EnrolState; due: string | null; percent: number; late: boolean; score: number | null; done_at: string | null } | null;
  people: { total: number; done: number; late: number } | null;
}
type EnrolState = 'assigned' | 'started' | 'done';
interface Question {
  id: string;
  text: string;
  options: string[];
  answer?: number;
}
interface Lesson {
  id: string;
  position: number;
  title: string;
  kind: 'text' | 'video' | 'quiz';
  body: string | null;
  url: string | null;
  questions: Question[] | null;
}
interface CourseDetail {
  course: Omit<CourseRow, 'lessons' | 'quizzes' | 'mine' | 'people' | 'skill_name' | 'skill_level_name'>;
  lessons: Lesson[];
  mine: { id: string; state: EnrolState; due: string | null; percent: number; score: number | null; done_at: string | null; progress: Record<string, { at: string; score?: number }> } | null;
  can: { manage: boolean };
}
interface QuizResult {
  score: number;
  passed: boolean;
  right: string[];
  wrong: string[];
}
type View = 'mine' | 'matrix' | 'courses' | 'kinds';

const toneOf = (n: number) => TONES[((n % TONES.length) + TONES.length) % TONES.length]!;
const KIND_ICON: Record<Lesson['kind'], IconName> = { text: 'book', video: 'eye', quiz: 'checkCircle' };

/** Level dots: filled up to the level, out of the number of levels. */
function LevelDots({ level, of }: { level: number; of: number }) {
  return (
    <span className="sk-dots" aria-hidden="true">
      {Array.from({ length: of }, (_, i) => (
        <span key={i} className={i <= level ? 'on' : ''} />
      ))}
    </span>
  );
}

export function SkillsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { can } = useSession();
  const manage = can('hr.manage');
  const [view, setView] = useState<View>('mine');
  const views: { key: View; icon: IconName; label: string; show: boolean }[] = [
    { key: 'mine', icon: 'contact', label: t('sk.v_mine'), show: true },
    { key: 'matrix', icon: 'table', label: t('sk.v_matrix'), show: true },
    { key: 'courses', icon: 'book', label: t('sk.v_courses'), show: true },
    { key: 'kinds', icon: 'sliders', label: t('sk.v_kinds'), show: manage },
  ];
  return (
    <section className="panel sk-panel">
      <div className="panel-head rev-head">
        <div className="segmented" role="tablist" aria-label={t('sk.views')}>
          {views
            .filter((v) => v.show)
            .map((v) => (
              <button key={v.key} role="tab" aria-selected={view === v.key} className={view === v.key ? 'active' : ''} onClick={() => setView(v.key)}>
                <Icon name={v.icon} size={16} />
                {v.label}
              </button>
            ))}
        </div>
      </div>
      {view === 'mine' ? <MySkills onKinds={manage ? () => setView('kinds') : undefined} /> : view === 'matrix' ? <Matrix /> : view === 'courses' ? <Courses onChange={onChange} /> : <Kinds />}
    </section>
  );
}

// ---- one person's skills -----------------------------------------------------------------

function MySkills({ onKinds }: { onKinds?: () => void }) {
  const { me } = useSession();
  const [user, setUser] = useState('');
  const { data, reload } = useAction<SkillsData>('hr.skills', { user: user || null });
  const { data: choices } = useAction<{ people: { id: string; name: string }[] }>('hr.directory_choices');
  const { busy, run } = useSave();
  const [q, setQ] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  if (!data) return <Skeleton h={260} r="var(--radius-lg)" />;
  const have = new Map(data.skills.map((s) => [s.skill, s]));
  const words = q.trim().toLowerCase();
  const set = (skill: string, level: number | null) =>
    void run(async () => {
      await action('hr.set_skill', { user: data.user.id, skill, level });
      await reload();
    });
  const self = data.user.id === me?.userId;
  return (
    <>
      <div className="rev-tools">
        <select className="input rev-who" value={user} aria-label={t('sk.skills_of')} onChange={(e) => setUser(e.target.value)}>
          <option value="">{t('sk.my_skills')}</option>
          {(choices?.people ?? [])
            .filter((p) => p.id !== me?.userId)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
        <label className="search sk-search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('sk.search_skills')} aria-label={t('sk.search_skills')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <label className="sk-only">
          <Switch checked={onlyMine} onChange={setOnlyMine} label={t('sk.only_known')} />
          <span className="small">{t('sk.only_known')}</span>
        </label>
        <span className="muted small">{tn('sk.known_n', data.skills.length)}</span>
      </div>
      {!data.can.edit ? (
        <p className="rev-guide">
          <Icon name="lock" size={16} />
          <span>{t('sk.read_only', { name: data.user.name })}</span>
        </p>
      ) : null}
      {!data.types.length ? (
        <EmptyState
          icon="sliders"
          title={t('sk.no_kinds')}
          body={onKinds ? t('sk.no_kinds_body_manage') : t('sk.no_kinds_body')}
          action={
            onKinds ? (
              <Button variant="primary" icon="plus" onClick={onKinds}>
                {t('sk.set_up_kinds')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="sk-kinds">
          {data.types.map((ty) => {
            const list = ty.skills.filter((s) => (!words || s.name.toLowerCase().includes(words)) && (!onlyMine || have.has(s.id)));
            if (!list.length) return null;
            return (
              <section key={ty.id} className="sk-kind">
                <h3>
                  <span className={`sk-kind-dot tone-${toneOf(ty.color)}`} aria-hidden="true" />
                  {ty.name}
                  <span className="muted small">{ty.levels.join(' · ')}</span>
                </h3>
                <ul>
                  {list.map((s) => {
                    const h = have.get(s.id);
                    return (
                      <li key={s.id} className={h ? 'known' : ''}>
                        <span className="sk-name">
                          <strong>{s.name}</strong>
                          {h?.note ? <span className="muted small">{h.note}</span> : null}
                        </span>
                        {data.can.edit ? (
                          <div className="sk-levels" role="radiogroup" aria-label={t('sk.level_of', { skill: s.name })}>
                            <button type="button" role="radio" aria-checked={!h} className={!h ? 'selected none' : 'none'} disabled={busy} onClick={() => h && set(s.id, null)}>
                              {t('sk.not_known')}
                            </button>
                            {ty.levels.map((l, i) => (
                              <button key={l} type="button" role="radio" aria-checked={h?.level === i} className={h?.level === i ? 'selected' : ''} disabled={busy} onClick={() => set(s.id, i)}>
                                {l}
                              </button>
                            ))}
                          </div>
                        ) : h ? (
                          <span className="sk-level-read">
                            <LevelDots level={h.level} of={ty.levels.length} />
                            {ty.levels[h.level]}
                          </span>
                        ) : (
                          <span className="muted small">—</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
      {self && data.can.edit && data.types.length ? <p className="muted small">{t('sk.mine_hint')}</p> : null}
    </>
  );
}

// ---- who knows what -----------------------------------------------------------------------

function Matrix() {
  const [type, setType] = useState('');
  const { data } = useAction<MatrixData>('hr.skills_matrix', {});
  const [dep, setDep] = useState('');
  const [skill, setSkill] = useState('');
  const [min, setMin] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const ty = data?.types.find((x) => x.id === type) ?? data?.types[0];
  const levels = useMemo(() => new Map((data?.levels ?? []).map((l) => [`${l.user}:${l.skill}`, l.level])), [data]);
  if (!data) return <Skeleton h={320} r="var(--radius-lg)" />;
  if (!ty) return <EmptyState icon="table" title={t('sk.no_kinds')} body={t('sk.no_kinds_body')} />;
  const people = data.people.filter((p) => (!dep || p.department === dep) && (!skill || (levels.get(`${p.id}:${skill}`) ?? -1) >= min));
  const skillName = ty.skills.find((s) => s.id === skill)?.name;
  return (
    <>
      <div className="rev-tools">
        <select
          className="input rev-who"
          value={ty.id}
          aria-label={t('sk.kind')}
          onChange={(e) => {
            setType(e.target.value);
            setSkill('');
            setMin(0);
          }}
        >
          {data.types.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
        <select className="input rev-who" value={dep} aria-label={t('sk.department')} onChange={(e) => setDep(e.target.value)}>
          <option value="">{t('sk.all_departments')}</option>
          {data.departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select className="input rev-who" value={skill} aria-label={t('sk.who_knows')} onChange={(e) => setSkill(e.target.value)}>
          <option value="">{t('sk.who_knows_any')}</option>
          {ty.skills.map((s) => (
            <option key={s.id} value={s.id}>
              {t('sk.who_knows_x', { skill: s.name })}
            </option>
          ))}
        </select>
        {skill ? (
          <select className="input rev-who" value={min} aria-label={t('sk.at_least')} onChange={(e) => setMin(Number(e.target.value))}>
            {ty.levels.map((l, i) => (
              <option key={l} value={i}>
                {t('sk.at_least_x', { level: l })}
              </option>
            ))}
          </select>
        ) : null}
        <span className="muted small">{tn('sk.people_n', people.length)}</span>
      </div>
      {skill ? <p className="muted small sk-matrix-note">{t('sk.matrix_filtered', { skill: skillName ?? '', level: ty.levels[min] ?? '' })}</p> : null}
      {people.length ? (
        <div className="hr-cal-wrap sk-matrix-wrap" tabIndex={0} aria-label={t('sk.matrix_scroll')}>
          <table className="sk-matrix">
            <thead>
              <tr>
                <th scope="col">{t('sk.person')}</th>
                {ty.skills.map((s) => (
                  <th key={s.id} scope="col" className={s.id === skill ? 'hl' : ''}>
                    <span>{s.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id}>
                  <th scope="row">
                    <button type="button" className="sk-person" onClick={() => setOpen(p.id)}>
                      <Avatar name={p.name} photo={p.photo} size="sm" />
                      <span>
                        <strong>{p.name}</strong>
                        {p.job_title ? <span className="muted small">{p.job_title}</span> : null}
                      </span>
                    </button>
                  </th>
                  {ty.skills.map((s) => {
                    const l = levels.get(`${p.id}:${s.id}`);
                    return (
                      <td key={s.id} className={s.id === skill ? 'hl' : ''}>
                        {l === undefined ? (
                          <span className="sk-none" aria-label={t('sk.not_known')}>
                            ·
                          </span>
                        ) : (
                          <span className={`sk-cell tone-${toneOf(ty.color)}`} title={`${s.name}: ${ty.levels[l]}`}>
                            <LevelDots level={l} of={ty.levels.length} />
                            <span className="sk-cell-word">{ty.levels[l]}</span>
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon="users" title={t('sk.nobody')} body={skill ? t('sk.nobody_body') : undefined} />
      )}
      {open ? <PersonSkillsDialog id={open} name={data.people.find((p) => p.id === open)?.name ?? ''} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function PersonSkillsDialog({ id, name, onClose }: { id: string; name: string; onClose: () => void }) {
  const { data } = useAction<SkillsData>('hr.skills', { user: id });
  return (
    <Dialog title={t('sk.skills_of_x', { name })} onClose={onClose} size="md">
      {!data ? (
        <Skeleton h={200} />
      ) : !data.skills.length ? (
        <p className="muted">{t('sk.none_yet', { name })}</p>
      ) : (
        <div className="sk-kinds compact">
          {data.types.map((ty) => {
            const list = ty.skills.filter((s) => data.skills.some((x) => x.skill === s.id));
            if (!list.length) return null;
            return (
              <section key={ty.id} className="sk-kind">
                <h3>
                  <span className={`sk-kind-dot tone-${toneOf(ty.color)}`} aria-hidden="true" />
                  {ty.name}
                </h3>
                <ul>
                  {list.map((s) => {
                    const h = data.skills.find((x) => x.skill === s.id)!;
                    return (
                      <li key={s.id} className="known">
                        <span className="sk-name">
                          <strong>{s.name}</strong>
                          {h.note ? <span className="muted small">{h.note}</span> : null}
                        </span>
                        <span className="sk-level-read">
                          <LevelDots level={h.level} of={ty.levels.length} />
                          {ty.levels[h.level]}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}

// ---- courses ------------------------------------------------------------------------------

function enrolBadge(m: CourseRow['mine']): { tone: Tone; icon: IconName; label: string } | null {
  if (!m) return null;
  if (m.state === 'done') return { tone: 'ok', icon: 'checkCircle', label: t('sk.e_done') };
  if (m.late) return { tone: 'danger', icon: 'alert', label: t('sk.e_late') };
  if (m.state === 'started') return { tone: 'info', icon: 'clock', label: t('sk.e_started', { n: m.percent }) };
  return { tone: 'warn', icon: 'bell', label: t('sk.e_assigned') };
}

function Courses({ onChange }: { onChange: () => Promise<void> }) {
  const { data, reload } = useAction<{ today: string; courses: CourseRow[]; can: { manage: boolean } }>('hr.courses');
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState<CourseRow | 'new' | null>(null);
  const changed = async () => {
    await reload();
    await onChange();
  };
  if (!data) return <Skeleton h={260} r="var(--radius-lg)" />;
  const todo = data.courses.filter((c) => c.mine && c.mine.state !== 'done');
  const rest = data.courses.filter((c) => !c.mine || c.mine.state === 'done');
  const card = (c: CourseRow) => {
    const b = enrolBadge(c.mine);
    return (
      <li key={c.id}>
        <button type="button" className={`sk-course${c.active ? '' : ' inactive'}`} onClick={() => setOpen(c.id)}>
          <span className={`sk-course-band tone-${toneOf(c.color)}`}>
            <Icon name="book" size={22} />
          </span>
          <span className="sk-course-body">
            <strong>{c.title}</strong>
            {c.description ? <span className="muted small sk-course-desc">{c.description}</span> : null}
            <span className="sk-course-meta small muted">
              <span>{tn('sk.lessons_n', c.lessons)}</span>
              {c.minutes ? <span>{t('sk.minutes', { n: c.minutes })}</span> : null}
              {c.skill_name ? (
                <span>
                  <Icon name="trendUp" size={12} /> {c.skill_level_name ? t('sk.raises', { skill: c.skill_name, level: c.skill_level_name }) : c.skill_name}
                </span>
              ) : null}
            </span>
            {c.mine && c.mine.state !== 'assigned' ? (
              <span className="rev-goal-progress">
                <span className="tk-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={c.mine.percent} aria-label={t('sk.progress_of', { title: c.title })}>
                  <span style={{ width: `${c.mine.percent}%` }} />
                </span>
                <span className="small">{c.mine.percent} %</span>
              </span>
            ) : null}
            <span className="sk-course-foot">
              {b ? <StatusBadge tone={b.tone} icon={b.icon} label={b.label} /> : null}
              {c.mine?.due && c.mine.state !== 'done' ? <span className={`small ${c.mine.late ? 'text-danger' : 'muted'}`}>{t('sk.by_day', { day: dayText(c.mine.due) })}</span> : null}
              {!c.active ? <StatusBadge tone="neutral" icon="archive" label={t('sk.not_in_use')} /> : null}
              {c.people ? (
                <span className="small muted sk-course-people">
                  <Icon name="users" size={12} /> {t('sk.people_done', { done: c.people.done, total: c.people.total })}
                  {c.people.late ? <span className="text-danger"> · {tn('sk.late_n', c.people.late)}</span> : null}
                </span>
              ) : null}
            </span>
          </span>
        </button>
      </li>
    );
  };
  return (
    <>
      <div className="rev-tools">
        <p className="muted small rev-tools-text">{t('sk.courses_hint')}</p>
        {data.can.manage ? (
          <Button variant="primary" icon="plus" onClick={() => setForm('new')}>
            {t('sk.new_course')}
          </Button>
        ) : null}
      </div>
      {!data.courses.length ? (
        <EmptyState
          icon="book"
          title={t('sk.no_courses')}
          body={data.can.manage ? t('sk.no_courses_body_manage') : t('sk.no_courses_body')}
          action={
            data.can.manage ? (
              <Button variant="primary" icon="plus" onClick={() => setForm('new')}>
                {t('sk.new_course')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {todo.length ? (
            <>
              <h3 className="sk-group">{t('sk.for_you')}</h3>
              <ul className="sk-courses">{todo.map(card)}</ul>
            </>
          ) : null}
          {rest.length ? (
            <>
              <h3 className="sk-group">{todo.length ? t('sk.all_courses') : t('sk.courses')}</h3>
              <ul className="sk-courses">{rest.map(card)}</ul>
            </>
          ) : null}
        </>
      )}
      {open ? <CourseDialog key={open} id={open} onClose={() => setOpen(null)} onChange={changed} /> : null}
      {form ? (
        <CourseForm
          initial={form === 'new' ? null : form}
          onClose={() => setForm(null)}
          onSaved={async (id) => {
            setForm(null);
            await reload();
            setOpen(id);
          }}
        />
      ) : null}
    </>
  );
}

/** The course: its lessons on the left, the open lesson on the right. Managers also edit it here. */
function CourseDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<CourseDetail>('hr.course', { id });
  const { busy, run } = useSave();
  const [at, setAt] = useState<string | null>(null);
  const [tab, setTab] = useState<'learn' | 'people'>('learn');
  const [edit, setEdit] = useState(false);
  const [lessonForm, setLessonForm] = useState<Lesson | 'new' | null>(null);
  const [assign, setAssign] = useState(false);
  const [remove, setRemove] = useState<Lesson | null>(null);
  const changed = async () => {
    await reload();
    await onChange();
  };
  if (!data) {
    return (
      <Dialog title={t('sk.course')} onClose={onClose} size="xl">
        <Skeleton h={360} r="var(--radius-lg)" />
      </Dialog>
    );
  }
  const { course, lessons, mine } = data;
  const done = (l: Lesson) => !!mine?.progress?.[l.id];
  const firstOpen = lessons.find((l) => !done(l)) ?? lessons[0];
  const current = lessons.find((l) => l.id === at) ?? firstOpen;
  const idx = current ? lessons.indexOf(current) : -1;
  const manage = data.can.manage;
  return (
    <Dialog
      title={course.title}
      onClose={onClose}
      size="xl"
      footer={
        manage ? (
          <span className="foot-actions rev-foot">
            <span className="sk-foot-left">
              <Button variant="ghost" icon="edit" onClick={() => setEdit(true)}>
                {t('sk.edit_course')}
              </Button>
              <Button variant="ghost" icon="plus" onClick={() => setLessonForm('new')}>
                {t('sk.add_lesson')}
              </Button>
            </span>
            <Button variant="secondary" icon="users" disabled={!course.active || !lessons.length} onClick={() => setAssign(true)}>
              {t('sk.assign')}
            </Button>
          </span>
        ) : undefined
      }
    >
      <div className="sk-course-head">
        <span className={`sk-course-band tone-${toneOf(course.color)}`}>
          <Icon name="book" size={22} />
        </span>
        <div>
          {course.description ? <p className="sk-course-text">{course.description}</p> : null}
          <span className="sk-course-meta small muted">
            <span>{tn('sk.lessons_n', lessons.length)}</span>
            {course.minutes ? <span>{t('sk.minutes', { n: course.minutes })}</span> : null}
            {lessons.some((l) => l.kind === 'quiz') ? <span>{t('sk.pass_mark', { n: course.pass_mark })}</span> : null}
            {mine?.due && mine.state !== 'done' ? <span>{t('sk.by_day', { day: dayText(mine.due) })}</span> : null}
          </span>
        </div>
        {mine ? (
          mine.state === 'done' ? (
            <StatusBadge tone="ok" icon="checkCircle" label={mine.done_at ? t('sk.done_on', { at: dateTime(mine.done_at) }) : t('sk.e_done')} />
          ) : (
            <span className="rev-goal-progress sk-head-progress">
              <span className="tk-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={mine.percent} aria-label={t('sk.progress_of', { title: course.title })}>
                <span style={{ width: `${mine.percent}%` }} />
              </span>
              <span className="small">{mine.percent} %</span>
            </span>
          )
        ) : null}
      </div>
      {manage ? (
        <div className="segmented sk-dialog-tabs" role="tablist" aria-label={t('sk.course')}>
          <button role="tab" aria-selected={tab === 'learn'} className={tab === 'learn' ? 'active' : ''} onClick={() => setTab('learn')}>
            <Icon name="book" size={16} />
            {t('sk.lessons')}
          </button>
          <button role="tab" aria-selected={tab === 'people'} className={tab === 'people' ? 'active' : ''} onClick={() => setTab('people')}>
            <Icon name="users" size={16} />
            {t('sk.who_takes_it')}
          </button>
        </div>
      ) : null}
      {tab === 'people' ? (
        <CoursePeople course={course.id} />
      ) : !lessons.length ? (
        <EmptyState
          icon="book"
          title={t('sk.no_lessons')}
          body={manage ? t('sk.no_lessons_body') : undefined}
          action={
            manage ? (
              <Button variant="primary" icon="plus" onClick={() => setLessonForm('new')}>
                {t('sk.add_lesson')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="sk-player">
          <ol className="sk-lessons">
            {lessons.map((l, i) => (
              <li key={l.id}>
                <button
                  type="button"
                  className={`sk-lesson${l.id === current?.id ? ' current' : ''}${done(l) ? ' done' : ''}`}
                  aria-current={l.id === current?.id ? 'step' : undefined}
                  onClick={() => setAt(l.id)}
                >
                  <span className="sk-lesson-n" aria-hidden="true">
                    {done(l) ? <Icon name="check" size={14} /> : i + 1}
                  </span>
                  <span className="sk-lesson-title">
                    {l.title}
                    <span className="muted small">
                      <Icon name={KIND_ICON[l.kind]} size={12} /> {t(`sk.kind_${l.kind}` as MessageKey)}
                      {l.kind === 'quiz' && mine?.progress?.[l.id]?.score !== undefined ? ` · ${mine.progress[l.id]!.score} %` : ''}
                    </span>
                  </span>
                </button>
                {manage && l.id === current?.id ? (
                  <span className="sk-lesson-tools">
                    <IconButton
                      icon="up"
                      label={t('sk.move_up')}
                      disabled={i === 0 || busy}
                      onClick={() =>
                        void run(async () => {
                          await action('hr.move_lesson', { id: l.id, by: -1 });
                          await reload();
                        })
                      }
                    />
                    <IconButton
                      icon="down"
                      label={t('sk.move_down')}
                      disabled={i === lessons.length - 1 || busy}
                      onClick={() =>
                        void run(async () => {
                          await action('hr.move_lesson', { id: l.id, by: 1 });
                          await reload();
                        })
                      }
                    />
                    <IconButton icon="edit" label={t('sk.edit_lesson')} onClick={() => setLessonForm(l)} />
                    <IconButton icon="trash" label={t('sk.remove_lesson')} onClick={() => setRemove(l)} />
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
          {current ? (
            <LessonView
              key={current.id}
              lesson={current}
              index={idx}
              total={lessons.length}
              done={done(current)}
              active={course.active}
              passMark={course.pass_mark}
              best={mine?.progress?.[current.id]?.score}
              onNext={idx < lessons.length - 1 ? () => setAt(lessons[idx + 1]!.id) : undefined}
              onDone={changed}
            />
          ) : null}
        </div>
      )}
      {edit ? (
        <CourseForm
          initial={{ ...course, lessons: lessons.length, quizzes: 0, mine: null, people: null, skill_name: null, skill_level_name: null }}
          onClose={() => setEdit(false)}
          onSaved={async () => {
            setEdit(false);
            await changed();
          }}
        />
      ) : null}
      {lessonForm ? (
        <LessonForm
          course={course.id}
          initial={lessonForm === 'new' ? null : lessonForm}
          onClose={() => setLessonForm(null)}
          onSaved={async () => {
            setLessonForm(null);
            await changed();
          }}
        />
      ) : null}
      {assign ? (
        <AssignDialog
          course={course.id}
          title={course.title}
          onClose={() => setAssign(false)}
          onSaved={async () => {
            setAssign(false);
            setTab('people');
            await changed();
          }}
        />
      ) : null}
      {remove ? (
        <Confirm
          title={t('sk.remove_lesson_title')}
          body={t('sk.remove_lesson_body', { title: remove.title })}
          confirm={t('sk.remove_lesson')}
          danger
          busy={busy}
          onClose={() => setRemove(null)}
          onConfirm={() =>
            void run(async () => {
              await action('hr.remove_lesson', { id: remove.id });
              setRemove(null);
              setAt(null);
              await changed();
            }, t('sk.lesson_removed'))
          }
        />
      ) : null}
    </Dialog>
  );
}

function LessonView({
  lesson,
  index,
  total,
  done,
  active,
  passMark,
  best,
  onNext,
  onDone,
}: {
  lesson: Lesson;
  index: number;
  total: number;
  done: boolean;
  active: boolean;
  passMark: number;
  best: number | undefined;
  onNext?: () => void;
  onDone: () => Promise<void>;
}) {
  const { toast } = useUi();
  const { busy, run } = useSave();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const qs = lesson.questions ?? [];
  const finish = () =>
    void run(async () => {
      const r = await action<{ quiz: QuizResult | null; course_done: boolean }>('hr.finish_lesson', { lesson: lesson.id, answers: lesson.kind === 'quiz' ? answers : null });
      if (r.quiz) setResult(r.quiz);
      if (r.course_done) toast(t('sk.course_done_toast'));
      await onDone();
      if (!r.quiz && onNext) onNext();
    });
  return (
    <article className="sk-lesson-view">
      <header>
        <span className="muted small">{t('sk.lesson_x_of_y', { n: index + 1, total })}</span>
        <h3>{lesson.title}</h3>
      </header>
      {lesson.kind === 'video' && lesson.url ? (
        <a className="sk-video" href={lesson.url} target="_blank" rel="noreferrer">
          <span className="sk-video-play">
            <Icon name="eye" size={24} />
          </span>
          <span>
            <strong>{t('sk.watch_video')}</strong>
            <span className="muted small">{new URL(lesson.url).hostname}</span>
          </span>
        </a>
      ) : null}
      {lesson.body ? <div className="sk-body">{lesson.body}</div> : null}
      {lesson.kind === 'quiz' ? (
        <>
          <p className="muted small">{best !== undefined ? t('sk.quiz_best', { n: best, pass: passMark }) : t('sk.quiz_hint', { pass: passMark })}</p>
          <ol className="sk-quiz">
            {qs.map((q, i) => {
              const state = result ? (result.right.includes(q.id) ? 'right' : 'wrong') : '';
              return (
                <li key={q.id} className={state}>
                  <fieldset>
                    <legend>
                      <span className="rev-q-n">{i + 1}</span>
                      {q.text}
                      {state === 'right' ? <StatusBadge tone="ok" icon="check" label={t('sk.right')} /> : state === 'wrong' ? <StatusBadge tone="danger" icon="x" label={t('sk.wrong')} /> : null}
                    </legend>
                    {q.options.map((o, j) => (
                      <label key={j} className={answers[q.id] === j ? 'on' : ''}>
                        <input
                          type="radio"
                          name={`q-${lesson.id}-${q.id}`}
                          checked={answers[q.id] === j}
                          onChange={() => {
                            setAnswers((a) => ({ ...a, [q.id]: j }));
                            setResult(null);
                          }}
                        />
                        <span>{o}</span>
                      </label>
                    ))}
                  </fieldset>
                </li>
              );
            })}
          </ol>
          {result ? (
            <p className={`rev-guide sk-result ${result.passed ? 'ok' : 'bad'}`} role="status">
              <Icon name={result.passed ? 'checkCircle' : 'alert'} size={16} />
              <span>{result.passed ? t('sk.passed', { n: result.score }) : t('sk.not_passed', { n: result.score, pass: passMark, wrong: result.wrong.length })}</span>
            </p>
          ) : null}
        </>
      ) : null}
      <footer className="sk-lesson-foot">
        {lesson.kind === 'quiz' ? (
          <>
            {result?.passed && onNext ? (
              <Button variant="secondary" icon="chevronRight" onClick={onNext}>
                {t('sk.next')}
              </Button>
            ) : null}
            <Button variant="primary" icon="send" busy={busy} disabled={!active || qs.some((q) => answers[q.id] === undefined)} onClick={finish}>
              {result && !result.passed ? t('sk.try_again') : t('sk.check_answers')}
            </Button>
          </>
        ) : done ? (
          <>
            <StatusBadge tone="ok" icon="checkCircle" label={t('sk.lesson_done')} />
            {onNext ? (
              <Button variant="secondary" icon="chevronRight" onClick={onNext}>
                {t('sk.next')}
              </Button>
            ) : null}
          </>
        ) : (
          <Button variant="primary" icon="check" busy={busy} disabled={!active} onClick={finish}>
            {onNext ? t('sk.done_next') : t('sk.done_finish')}
          </Button>
        )}
      </footer>
    </article>
  );
}

function CoursePeople({ course }: { course: string }) {
  const { data } = useAction<{ id: string; name: string; state: EnrolState; due: string | null; late: boolean; percent: number; score: number | null; done_at: string | null }[]>('hr.course_people', {
    course,
  });
  if (!data) return <Skeleton h={200} />;
  if (!data.length) return <EmptyState icon="users" title={t('sk.nobody_takes')} body={t('sk.nobody_takes_body')} />;
  return (
    <ul className="rev-goals">
      {data.map((e) => (
        <li key={e.id} className={`rev-goal is-${e.state === 'done' ? 'done' : 'open'}`}>
          <div className="rev-goal-open">
            <span className="rev-goal-main">
              <span className="sk-person">
                <Avatar name={e.name} photo={null} size="sm" />
                <strong>{e.name}</strong>
              </span>
              <span className="rev-goal-meta small">
                {e.state === 'done' ? (
                  <StatusBadge tone="ok" icon="checkCircle" label={e.done_at ? t('sk.done_on', { at: dateTime(e.done_at) }) : t('sk.e_done')} />
                ) : e.due ? (
                  <span className={e.late ? 'text-danger' : 'muted'}>{e.late ? t('sk.late_since', { day: dayText(e.due) }) : t('sk.by_day', { day: dayText(e.due) })}</span>
                ) : (
                  <span className="muted">{e.state === 'assigned' ? t('sk.e_assigned') : t('sk.no_last_day')}</span>
                )}
                {e.score !== null ? <span className="muted"> · {t('sk.score', { n: e.score })}</span> : null}
              </span>
            </span>
            <span className="rev-goal-progress">
              <span
                className={`tk-progress${e.late ? ' warn' : ''}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={e.percent}
                aria-label={t('sk.progress_of', { title: e.name })}
              >
                <span style={{ width: `${e.percent}%` }} />
              </span>
              <span className="small">{e.percent} %</span>
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function AssignDialog({ course, title, onClose, onSaved }: { course: string; title: string; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: dir } = useAction<{ people: { id: string; name: string; job_title: string | null; photo: string | null }[] }>('hr.directory');
  const { toast } = useUi();
  const { busy, run } = useSave();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [due, setDue] = useState('');
  const [q, setQ] = useState('');
  const shown = (dir?.people ?? []).filter((p) => !q.trim() || `${p.name} ${p.job_title ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Dialog
      title={t('sk.assign_title', { title })}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <span className="muted small">{tn('rev.people_picked', picked.size)}</span>
          <Button
            variant="primary"
            icon="send"
            busy={busy}
            disabled={!picked.size}
            onClick={() =>
              void run(async () => {
                const r = await action<{ added: number; updated: number }>('hr.assign_course', { course, people: [...picked], due: due || null });
                toast(t('sk.assigned_toast', { added: r.added, updated: r.updated }));
                await onSaved();
              })
            }
          >
            {t('sk.assign_n', { n: picked.size })}
          </Button>
        </span>
      }
    >
      {!dir ? (
        <Skeleton h={260} />
      ) : (
        <div className="form-stack">
          <Field label={t('sk.last_day')} hint={t('sk.last_day_hint')}>
            <input className="input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
          <fieldset className="rev-pick">
            <legend className="field-label">{t('sk.who_takes_it')}</legend>
            <div className="rev-pick-tools">
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" value={q} placeholder={t('rev.search_people')} aria-label={t('rev.search_people')} onChange={(e) => setQ(e.target.value)} />
              </label>
              <Button size="sm" variant="ghost" onClick={() => setPicked(new Set([...picked, ...shown.map((p) => p.id)]))}>
                {t('rev.pick_all')}
              </Button>
              <Button size="sm" variant="ghost" disabled={!picked.size} onClick={() => setPicked(new Set())}>
                {t('rev.pick_none')}
              </Button>
            </div>
            <ul className="rev-pick-list">
              {shown.map((p) => (
                <li key={p.id}>
                  <label className={picked.has(p.id) ? 'on' : ''}>
                    <input
                      type="checkbox"
                      checked={picked.has(p.id)}
                      onChange={() =>
                        setPicked((s) => {
                          const n = new Set(s);
                          if (n.has(p.id)) n.delete(p.id);
                          else n.add(p.id);
                          return n;
                        })
                      }
                    />
                    <Avatar name={p.name} photo={p.photo} size="sm" />
                    <span className="rev-pick-who">
                      <strong>{p.name}</strong>
                      {p.job_title ? <span className="muted small">{p.job_title}</span> : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        </div>
      )}
    </Dialog>
  );
}

function CourseForm({ initial, onClose, onSaved }: { initial: CourseRow | null; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { data: sk } = useAction<SkillsData>('hr.skills', {});
  const { busy, run } = useSave();
  const [f, setF] = useState({
    title: initial?.title ?? '',
    description: initial?.description ?? '',
    color: initial?.color ?? 0,
    minutes: initial?.minutes ? String(initial.minutes) : '',
    pass_mark: initial?.pass_mark ?? 80,
    skill: initial?.skill ?? '',
    skill_level: initial?.skill_level ?? 0,
    active: initial?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const ok = f.title.trim().length >= 3;
  const ty = sk?.types.find((x) => x.skills.some((s) => s.id === f.skill));
  return (
    <Dialog
      title={initial ? t('sk.edit_course') : t('sk.new_course')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!ok) return;
              void run(async () => {
                const c = await action<{ id: string }>('hr.save_course', {
                  id: initial?.id ?? null,
                  title: f.title.trim(),
                  description: f.description.trim() || null,
                  color: f.color,
                  minutes: f.minutes ? Number(f.minutes) : null,
                  pass_mark: f.pass_mark,
                  skill: f.skill || null,
                  skill_level: f.skill ? f.skill_level : null,
                  active: f.active,
                });
                await onSaved(c.id);
              }, t('sk.course_saved'));
            }}
          >
            {t('rev.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('sk.course_title')} error={tried && !ok ? t('sk.e_course_title') : null} required>
          <input className="input" value={f.title} maxLength={100} placeholder={t('sk.course_title_ph')} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <Field label={t('sk.course_description')} hint={t('rev.optional')}>
          <textarea className="input" rows={3} maxLength={2000} value={f.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <div className="form-grid">
          <Field label={t('sk.course_minutes')} hint={t('rev.optional')}>
            <input className="input" type="number" inputMode="numeric" min={1} value={f.minutes} onChange={(e) => set('minutes', e.target.value)} />
          </Field>
          <Field label={t('sk.course_pass_mark', { n: f.pass_mark })} hint={t('sk.course_pass_hint')}>
            <input className="rev-range" type="range" min={0} max={100} step={5} value={f.pass_mark} onChange={(e) => set('pass_mark', Number(e.target.value))} />
          </Field>
          <Field label={t('sk.course_skill')} hint={t('sk.course_skill_hint')}>
            <select className="input" value={f.skill} onChange={(e) => set('skill', e.target.value)}>
              <option value="">—</option>
              {(sk?.types ?? []).map((x) => (
                <optgroup key={x.id} label={x.name}>
                  {x.skills.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          {ty ? (
            <Field label={t('sk.course_level')}>
              <select className="input" value={Math.min(f.skill_level, ty.levels.length - 1)} onChange={(e) => set('skill_level', Number(e.target.value))}>
                {ty.levels.map((l, i) => (
                  <option key={l} value={i}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
        <fieldset className="dir-colors">
          <legend className="field-label">{t('dir.color')}</legend>
          {TONES.map((tone, i) => (
            <button
              type="button"
              key={tone}
              className={`dir-color tone-${tone}${f.color === i ? ' selected' : ''}`}
              aria-pressed={f.color === i}
              aria-label={t(`dir.color_${tone}` as MessageKey)}
              onClick={() => set('color', i)}
            >
              {f.color === i ? <Icon name="check" size={14} /> : null}
            </button>
          ))}
        </fieldset>
        <div className="rev-switch-field">
          <Switch checked={f.active} onChange={(v) => set('active', v)} label={t('rev.in_use')} />
          <span>
            <strong>{t('rev.in_use')}</strong>
            <span className="muted small">{t('sk.course_in_use_hint')}</span>
          </span>
        </div>
      </div>
    </Dialog>
  );
}

function LessonForm({ course, initial, onClose, onSaved }: { course: string; initial: Lesson | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [kind, setKind] = useState<Lesson['kind']>(initial?.kind ?? 'text');
  const [body, setBody] = useState(initial?.body ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [qs, setQs] = useState<Required<Question>[]>((initial?.questions as Required<Question>[] | null) ?? [{ id: 'q1', text: '', options: ['', ''], answer: 0 }]);
  const [tried, setTried] = useState(false);
  const setQ = (i: number, v: Partial<Required<Question>>) => setQs((x) => x.map((q, j) => (j === i ? { ...q, ...v } : q)));
  const nextId = () => `q${Math.max(0, ...qs.map((q) => Number(q.id.replace(/\D/g, '')) || 0)) + 1}`;
  const okTitle = title.trim().length >= 2;
  const okBody = kind !== 'text' || body.trim().length > 0;
  const okUrl = kind !== 'video' || /^https:\/\/\S+$/.test(url.trim());
  const okQs = kind !== 'quiz' || (qs.length > 0 && qs.every((q) => q.text.trim().length >= 3 && q.options.length >= 2 && q.options.every((o) => o.trim())));
  return (
    <Dialog
      title={initial ? t('sk.edit_lesson') : t('sk.add_lesson')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!okTitle || !okBody || !okUrl || !okQs) return;
              void run(async () => {
                await action('hr.save_lesson', {
                  id: initial?.id ?? null,
                  course,
                  title: title.trim(),
                  kind,
                  body: body.trim() || null,
                  url: kind === 'video' ? url.trim() : null,
                  questions: kind === 'quiz' ? qs.map((q) => ({ ...q, text: q.text.trim(), options: q.options.map((o) => o.trim()) })) : null,
                });
                await onSaved();
              }, t('sk.lesson_saved'));
            }}
          >
            {t('rev.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <div className="segmented" role="radiogroup" aria-label={t('sk.lesson_kind')}>
          {(['text', 'video', 'quiz'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              <Icon name={KIND_ICON[k]} size={16} />
              {t(`sk.kind_${k}` as MessageKey)}
            </button>
          ))}
        </div>
        <Field label={t('sk.lesson_title')} error={tried && !okTitle ? t('sk.e_lesson_title') : null} required>
          <input className="input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} data-autofocus />
        </Field>
        {kind === 'video' ? (
          <Field label={t('sk.video_link')} hint={t('sk.video_link_hint')} error={tried && !okUrl ? t('sk.e_video_link') : null} required>
            <input className="input" type="url" inputMode="url" value={url} placeholder="https://" onChange={(e) => setUrl(e.target.value)} />
          </Field>
        ) : null}
        <Field
          label={kind === 'text' ? t('sk.lesson_text') : t('sk.lesson_note')}
          hint={kind === 'text' ? t('sk.lesson_text_hint') : t('rev.optional')}
          error={tried && !okBody ? t('sk.e_lesson_text') : null}
          required={kind === 'text'}
        >
          <textarea className="input" rows={kind === 'text' ? 10 : 3} maxLength={20_000} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        {kind === 'quiz' ? (
          <fieldset className="rev-q-editor">
            <legend className="field-label">{t('rev.questions')}</legend>
            {tried && !okQs ? (
              <span className="field-msg" role="alert">
                {t('sk.e_quiz')}
              </span>
            ) : null}
            <ol>
              {qs.map((q, i) => (
                <li key={q.id}>
                  <span className="rev-q-n">{i + 1}</span>
                  <div className="rev-q-fields">
                    <input
                      className="input"
                      value={q.text}
                      maxLength={300}
                      placeholder={t('sk.question_ph')}
                      aria-label={t('rev.question_n', { n: i + 1 })}
                      onChange={(e) => setQ(i, { text: e.target.value })}
                    />
                    <div className="sk-options" role="radiogroup" aria-label={t('sk.right_answer_of', { n: i + 1 })}>
                      {q.options.map((o, j) => (
                        <div key={j} className={`sk-option${q.answer === j ? ' right' : ''}`}>
                          <input type="radio" name={`ans-${q.id}`} checked={q.answer === j} aria-label={t('sk.mark_right', { n: j + 1 })} onChange={() => setQ(i, { answer: j })} />
                          <input
                            className="input"
                            value={o}
                            maxLength={200}
                            placeholder={t('sk.answer_n', { n: j + 1 })}
                            aria-label={t('sk.answer_n', { n: j + 1 })}
                            onChange={(e) => setQ(i, { options: q.options.map((x, k) => (k === j ? e.target.value : x)) })}
                          />
                          <IconButton
                            icon="x"
                            label={t('sk.remove_answer')}
                            disabled={q.options.length <= 2}
                            onClick={() => setQ(i, { options: q.options.filter((_, k) => k !== j), answer: q.answer === j ? 0 : q.answer > j ? q.answer - 1 : q.answer })}
                          />
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" icon="plus" disabled={q.options.length >= 6} onClick={() => setQ(i, { options: [...q.options, ''] })}>
                        {t('sk.add_answer')}
                      </Button>
                    </div>
                  </div>
                  <span className="rev-q-move">
                    <IconButton icon="trash" label={t('rev.remove_question')} disabled={qs.length === 1} onClick={() => setQs((x) => x.filter((_, j) => j !== i))} />
                  </span>
                </li>
              ))}
            </ol>
            <span className="muted small">{t('sk.quiz_editor_hint')}</span>
            <Button size="sm" variant="secondary" icon="plus" disabled={qs.length >= 20} onClick={() => setQs((x) => [...x, { id: nextId(), text: '', options: ['', ''], answer: 0 }])}>
              {t('rev.add_question')}
            </Button>
          </fieldset>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- kinds of skill -----------------------------------------------------------------------

function Kinds() {
  const { data, reload } = useAction<SkillsData>('hr.skills', {});
  const { data: matrix } = useAction<MatrixData>('hr.skills_matrix', {});
  const [form, setForm] = useState<SkillType | 'new' | null>(null);
  const { busy, run } = useSave();
  if (!data) return <Skeleton h={220} r="var(--radius-lg)" />;
  const usedBy = (ty: SkillType) => new Set((matrix?.levels ?? []).filter((l) => ty.skills.some((s) => s.id === l.skill)).map((l) => l.user)).size;
  return (
    <>
      <div className="rev-tools">
        <p className="muted small rev-tools-text">{t('sk.kinds_hint')}</p>
        <Button variant="primary" icon="plus" onClick={() => setForm('new')}>
          {t('sk.new_kind')}
        </Button>
      </div>
      {data.types.length ? (
        <ul className="rev-templates">
          {data.types.map((ty) => (
            <li key={ty.id}>
              <button type="button" className="rev-template" onClick={() => setForm(ty)}>
                <span className={`rev-template-icon tone-${toneOf(ty.color)}`}>
                  <Icon name="sliders" size={18} />
                </span>
                <span className="rev-template-main">
                  <strong>{ty.name}</strong>
                  <span className="muted small">
                    {ty.levels.join(' → ')} · {tn('sk.skills_n', ty.skills.length)}
                  </span>
                </span>
                <span className="muted small">{tn('sk.people_n', usedBy(ty))}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon="sliders"
          title={t('sk.no_kinds')}
          body={t('sk.no_kinds_body_manage')}
          action={
            <Button
              variant="primary"
              icon="plus"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await action('hr.add_default_skill_types');
                  await reload();
                }, t('sk.kinds_added'))
              }
            >
              {t('sk.add_defaults')}
            </Button>
          }
        />
      )}
      {form ? (
        <KindForm
          initial={form === 'new' ? null : form}
          onClose={() => setForm(null)}
          onSaved={async () => {
            setForm(null);
            await reload();
          }}
        />
      ) : null}
    </>
  );
}

function KindForm({ initial, onClose, onSaved }: { initial: SkillType | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState(initial?.color ?? 0);
  const [levels, setLevels] = useState<string[]>(initial?.levels ?? ['Learning', 'Can do it', 'Can teach it']);
  const [list, setList] = useState<{ id: string | null; name: string }[]>(initial?.skills.map((s) => ({ id: s.id, name: s.name })) ?? [{ id: null, name: '' }]);
  const [tried, setTried] = useState(false);
  const [remove, setRemove] = useState(false);
  const okName = name.trim().length >= 2;
  const okLevels = levels.length > 0 && levels.every((l) => l.trim());
  const cleanSkills = list.filter((s) => s.name.trim());
  const fewer = initial && levels.length < initial.levels.length;
  return (
    <Dialog
      title={initial ? t('sk.edit_kind') : t('sk.new_kind')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions rev-foot">
          {initial ? (
            <Button variant="ghost" icon="trash" onClick={() => setRemove(true)}>
              {t('sk.remove_kind')}
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!okName || !okLevels) return;
              void run(async () => {
                await action('hr.save_skill_type', {
                  id: initial?.id ?? null,
                  name: name.trim(),
                  color,
                  levels: levels.map((l) => l.trim()),
                  skills: cleanSkills.map((s) => ({ id: s.id, name: s.name.trim() })),
                });
                await onSaved();
              }, t('sk.kind_saved'));
            }}
          >
            {t('rev.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('sk.kind_name')} error={tried && !okName ? t('sk.e_kind_name') : null} required>
          <input className="input" value={name} maxLength={60} placeholder={t('sk.kind_name_ph')} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <fieldset className="dir-colors">
          <legend className="field-label">{t('dir.color')}</legend>
          {TONES.map((tone, i) => (
            <button
              type="button"
              key={tone}
              className={`dir-color tone-${tone}${color === i ? ' selected' : ''}`}
              aria-pressed={color === i}
              aria-label={t(`dir.color_${tone}` as MessageKey)}
              onClick={() => setColor(i)}
            >
              {color === i ? <Icon name="check" size={14} /> : null}
            </button>
          ))}
        </fieldset>
        <div className="sk-edit-cols">
          <fieldset className="rev-q-editor">
            <legend className="field-label">{t('sk.levels')}</legend>
            <span className="muted small">{t('sk.levels_hint')}</span>
            {tried && !okLevels ? (
              <span className="field-msg" role="alert">
                {t('sk.e_levels')}
              </span>
            ) : null}
            <ol>
              {levels.map((l, i) => (
                <li key={i} className="sk-row">
                  <span className="rev-q-n">{i + 1}</span>
                  <input className="input" value={l} maxLength={30} aria-label={t('sk.level_n', { n: i + 1 })} onChange={(e) => setLevels((x) => x.map((y, j) => (j === i ? e.target.value : y)))} />
                  <IconButton icon="trash" label={t('sk.remove_level')} disabled={levels.length === 1} onClick={() => setLevels((x) => x.filter((_, j) => j !== i))} />
                </li>
              ))}
            </ol>
            {fewer ? <span className="muted small">{t('sk.fewer_levels')}</span> : null}
            <Button size="sm" variant="secondary" icon="plus" disabled={levels.length >= 6} onClick={() => setLevels((x) => [...x, ''])}>
              {t('sk.add_level')}
            </Button>
          </fieldset>
          <fieldset className="rev-q-editor">
            <legend className="field-label">{t('sk.skills')}</legend>
            <span className="muted small">{t('sk.skills_hint')}</span>
            <ol>
              {list.map((s, i) => (
                <li key={s.id ?? `n${i}`} className="sk-row">
                  <span className="rev-q-n">{i + 1}</span>
                  <input
                    className="input"
                    value={s.name}
                    maxLength={60}
                    aria-label={t('sk.skill_n', { n: i + 1 })}
                    onChange={(e) => setList((x) => x.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)))}
                  />
                  <IconButton icon="trash" label={t('sk.remove_skill')} onClick={() => setList((x) => x.filter((_, j) => j !== i))} />
                </li>
              ))}
            </ol>
            <Button size="sm" variant="secondary" icon="plus" onClick={() => setList((x) => [...x, { id: null, name: '' }])}>
              {t('sk.add_skill')}
            </Button>
          </fieldset>
        </div>
      </div>
      {remove && initial ? (
        <Confirm
          title={t('sk.remove_kind_title', { name: initial.name })}
          body={t('sk.remove_kind_body')}
          confirm={t('sk.remove_kind')}
          danger
          busy={busy}
          onClose={() => setRemove(false)}
          onConfirm={() =>
            void run(async () => {
              await action('hr.remove_skill_type', { id: initial.id });
              setRemove(false);
              await onSaved();
            }, t('sk.kind_removed'))
          }
        />
      ) : null}
    </Dialog>
  );
}
